import { DOCUMENT, DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/** Chromiumov `beforeinstallprompt` (nije u lib.dom.d.ts). */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export type IshodInstalacije = 'prihvaceno' | 'odbijeno' | 'nedostupno';

/** Što se dogodilo s instalacijom (za praćenje broja instalacija; bez osobnih podataka). */
export type DogadajInstalacije = 'prihvaceno' | 'odbijeno' | 'appinstalled';

/** Platforma uređaja kao kod (isti kodovi kao backend `Platforma`); user-agent se nikamo ne šalje. */
export type PlatformaUredaja = 'IOS' | 'ANDROID' | 'DESKTOP' | 'OSTALO';

/** Preglednik na iPhoneu/iPadu (za upute „Dodaj na početni zaslon”). */
export type IosPreglednik = 'SAFARI' | 'CHROME' | 'EDGE' | 'FIREFOX' | 'DRUGI';

/**
 * Instalacija aplikacije na početni zaslon.
 *
 * - Android / Chromium: preglednik javlja `beforeinstallprompt` kad je stranica instalabilna
 *   (manifest postoji samo na `/dashboard`). Događaj čuvamo i nudimo diskretan gumb; vlastiti
 *   preglednikov banner ne prikazujemo (`preventDefault`).
 * - iOS: nema `beforeinstallprompt`; nudi se samo kratka uputa (Dijeli → Dodaj na početni zaslon).
 * - Već instalirano (otvoreno kao aplikacija ili `appinstalled`): ne nudi se ništa.
 *
 * Servis se stvara pri pokretanju aplikacije (`providePwa`), jer `beforeinstallprompt` može
 * stići prije nego što se otvori ekran s gumbom.
 */
@Injectable({ providedIn: 'root' })
export class PwaInstalacijaService {
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private readonly dogadaj = signal<BeforeInstallPromptEvent | null>(null);
  private readonly _instalirano = signal(false);

  private readonly _dogadaji = new Subject<DogadajInstalacije>();

  readonly instalirano = this._instalirano.asReadonly();
  readonly ios: boolean;
  /** iOS i Safari (jedini preglednik na iOS-u za koji dajemo upute „Dijeli → Dodaj na početni zaslon”). */
  readonly iosSafari: boolean;
  /** Preglednik na iOS-u (`DRUGI` i izvan iOS-a). */
  readonly iosPreglednik: IosPreglednik;
  /**
   * Chrome, Edge ili Firefox na iOS-u 16.4+: „Dodaj na početni zaslon” postoji u izborniku Dijeli tog preglednika
   * (prije 16.4 samo Safari).
   */
  readonly iosDodajUPregledniku: boolean;
  readonly platforma: PlatformaUredaja;
  /** Stranica je pri pokretanju otvorena kao instalirana aplikacija (ne mijenja se tijekom rada). */
  readonly pokrenutoKaoAplikacija: boolean;
  /**
   * Ishod ponude preglednika (`prihvaceno` / `odbijeno`) i `appinstalled`. Emitira i kad ponudu otvori bilo koji gumb
   * (onboarding, postavke, ljuska), pa praćenje ne propusti nijednu instalaciju.
   */
  readonly dogadaji: Observable<DogadajInstalacije> = this._dogadaji.asObservable();
  /** Gumb „Instaliraj aplikaciju” (samo kad je preglednik ponudio instalaciju). */
  readonly mozeInstalirati = computed(() => this.dogadaj() !== null && !this._instalirano());
  /** Uputa za iPhone/iPad (Safari nema gumb za instalaciju). */
  readonly prikaziIosUputu = computed(() => this.ios && !this._instalirano());

  constructor() {
    const win = this.doc.defaultView;
    const nav = win?.navigator;
    this.ios = !!nav && jeIos(nav.userAgent, nav.maxTouchPoints);
    this.iosSafari = this.ios && jeIosSafari(nav?.userAgent ?? '');
    this.iosPreglednik = this.ios ? iosPreglednik(nav?.userAgent ?? '') : 'DRUGI';
    this.iosDodajUPregledniku = this.ios && dodajUPregledniku(nav?.userAgent ?? '');
    this.platforma = nav ? platformaUredaja(nav.userAgent, nav.maxTouchPoints) : 'OSTALO';
    this.pokrenutoKaoAplikacija = !!win && jeSamostalno(win);
    if (!win) {
      return;
    }
    this._instalirano.set(this.pokrenutoKaoAplikacija);

    const naPrompt = (e: Event): void => {
      e.preventDefault();
      if (!this._instalirano()) {
        this.dogadaj.set(e as BeforeInstallPromptEvent);
      }
    };
    const naInstalirano = (): void => {
      this._instalirano.set(true);
      this.dogadaj.set(null);
    };
    const naAppinstalled = (): void => {
      naInstalirano();
      this._dogadaji.next('appinstalled');
    };
    win.addEventListener('beforeinstallprompt', naPrompt);
    win.addEventListener('appinstalled', naAppinstalled);

    const upit = win.matchMedia?.('(display-mode: standalone)');
    const naNacin = (e: MediaQueryListEvent): void => {
      if (e.matches) {
        naInstalirano();
      }
    };
    upit?.addEventListener?.('change', naNacin);

    this.destroyRef.onDestroy(() => {
      win.removeEventListener('beforeinstallprompt', naPrompt);
      win.removeEventListener('appinstalled', naAppinstalled);
      upit?.removeEventListener?.('change', naNacin);
    });
  }

  /**
   * Otvara preglednikov dijalog instalacije. Događaj vrijedi samo jednom (i kad korisnik odbije),
   * pa se gumb skriva dok preglednik ne pošalje novi.
   */
  async instaliraj(): Promise<IshodInstalacije> {
    const e = this.dogadaj();
    if (!e) {
      return 'nedostupno';
    }
    this.dogadaj.set(null);
    try {
      await e.prompt();
      const izbor = await e.userChoice;
      if (izbor.outcome === 'accepted') {
        this._instalirano.set(true);
        this._dogadaji.next('prihvaceno');
        return 'prihvaceno';
      }
      this._dogadaji.next('odbijeno');
      return 'odbijeno';
    } catch {
      return 'nedostupno';
    }
  }
}

/** iPhone, iPod, iPad (i iPadOS koji se predstavlja kao Mac, ali ima dodirni zaslon). */
export function jeIos(userAgent: string, maxTouchPoints = 0): boolean {
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return true;
  }
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

/**
 * Safari na iOS-u. Ostali preglednici na iOS-u (Chrome, Firefox, Edge, Opera, Google app, preglednici unutar
 * Facebooka, Instagrama…) imaju svoje oznake u user-agentu; za njih nudimo „Otvorite u Safariju”.
 */
export function jeIosSafari(userAgent: string): boolean {
  return /Safari\//.test(userAgent)
    && !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|GSA\/|YaBrowser|DuckDuckGo|FBAN|FBAV|Instagram|Line\/|Snapchat|MicroMessenger/i.test(userAgent);
}

/** Preglednik na iOS-u iz user-agenta (Chrome `CriOS`, Edge `EdgiOS`, Firefox `FxiOS`). */
export function iosPreglednik(userAgent: string): IosPreglednik {
  if (jeIosSafari(userAgent)) return 'SAFARI';
  if (/CriOS\//.test(userAgent)) return 'CHROME';
  if (/EdgiOS\//.test(userAgent)) return 'EDGE';
  if (/FxiOS\//.test(userAgent)) return 'FIREFOX';
  return 'DRUGI';
}

/** Verzija iOS-a iz user-agenta („iPhone OS 17_5”, „CPU OS 16_4”); null kad je nema (npr. iPad kao Mac). */
export function iosVerzija(userAgent: string): [number, number] | null {
  const m = /\bOS (\d+)_(\d+)/.exec(userAgent);
  return m ? [Number(m[1]), Number(m[2])] : null;
}

/** Chrome, Edge ili Firefox na iOS-u 16.4 ili novijem (nepoznata verzija: ne). */
export function dodajUPregledniku(userAgent: string): boolean {
  const p = iosPreglednik(userAgent);
  if (p !== 'CHROME' && p !== 'EDGE' && p !== 'FIREFOX') return false;
  const v = iosVerzija(userAgent);
  return !!v && (v[0] > 16 || (v[0] === 16 && v[1] >= 4));
}

/** Platforma iz user-agenta (samo na uređaju; šalje se samo kod). */
export function platformaUredaja(userAgent: string, maxTouchPoints = 0): PlatformaUredaja {
  if (jeIos(userAgent, maxTouchPoints)) return 'IOS';
  if (/Android/i.test(userAgent)) return 'ANDROID';
  if (/Mobi|Tablet|Opera Mini|KAIOS|Silk/i.test(userAgent)) return 'OSTALO';
  return 'DESKTOP';
}

/** Stranica je otvorena kao instalirana aplikacija (Android: display-mode, iOS: navigator.standalone). */
export function jeSamostalno(win: Window): boolean {
  const standalone = (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone || !!win.matchMedia?.('(display-mode: standalone)').matches;
}
